import { z } from 'zod'

// Org-managed vendor payment terms — per-org lookup that replaces the
// hardcoded PAYMENT_TERMS arrays in the vendor dialogs. Suppliers store the
// chosen option as a string code on `Supplier.paymentTermCode`; the term's
// `netDays` is denormalized onto `Supplier.paymentTerms` for analytics/CSV.
// `netDays` is null for terms without a fixed day count (Annual, COD).

export const SupplierPaymentTermSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string().min(1).max(100),
  code: z.string().min(1).max(50),
  netDays: z.number().int().min(0).max(3650).optional().nullable(),
  description: z.string().max(255).optional().nullable(),
  isSystem: z.boolean().default(false),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type SupplierPaymentTerm = z.infer<typeof SupplierPaymentTermSchema>

export const CreateSupplierPaymentTermSchema = SupplierPaymentTermSchema.omit({
  id: true,
  organizationId: true,
  isSystem: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Optional — the API derives an uppercased slug from the name when omitted
  // (used by the inline "+ New term" dialog in the vendor forms).
  code: z.string().min(1).max(50).optional(),
})
export type CreateSupplierPaymentTerm = z.infer<
  typeof CreateSupplierPaymentTermSchema
>

export const UpdateSupplierPaymentTermSchema =
  CreateSupplierPaymentTermSchema.partial()
export type UpdateSupplierPaymentTerm = z.infer<
  typeof UpdateSupplierPaymentTermSchema
>

// Defaults lazily seeded for each org (isSystem=true rows: deactivate-only,
// no rename/delete). Order here defines sortOrder.
export const DEFAULT_SUPPLIER_PAYMENT_TERMS: Array<{
  name: string
  code: string
  netDays: number | null
}> = [
  { name: 'Due on Receipt', code: 'DUE_ON_RECEIPT', netDays: 0 },
  { name: 'COD', code: 'COD', netDays: null },
  { name: 'Net 15', code: 'NET_15', netDays: 15 },
  { name: 'Net 30', code: 'NET_30', netDays: 30 },
  { name: 'Net 45', code: 'NET_45', netDays: 45 },
  { name: 'Net 60', code: 'NET_60', netDays: 60 },
  { name: 'Net 90', code: 'NET_90', netDays: 90 },
  { name: 'Annual', code: 'ANNUAL', netDays: null },
]
